import { useState, useCallback, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { useCurrentWorkspace } from './workspace';

/* ── Types ──────────────────────────────────────────────────────── */

export interface DominaImage {
  publicId: string;
  filename: string;
  url: string;
}

export interface DominaMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  confident?: boolean;
  timestamp: string;
  /** Names of files the user attached to this message (for display only). */
  attachments?: { name: string }[];
  /** Images Domina generated in this turn (shown inline). */
  images?: DominaImage[];
}

interface DominaApiResponse {
  reply: string;
  confident: boolean;
  images?: DominaImage[];
}

export interface DominaStatus {
  available: boolean;
  /** Our own internal workspaces — unlimited, no credits needed. */
  unlimited: boolean;
  /** Purchased AI-credit balance remaining. */
  balance: number;
  quota: { used: number; cap: number; remaining: number; plan: string };
  /** Domina is out of credits (and plan allowance) — usage is blocked. */
  blocked: boolean;
}

/** Domina entitlement for the current workspace (credit balance, blocked, etc.). */
export function useDominaStatus() {
  const ws = useCurrentWorkspace();
  return useQuery({
    queryKey: ['domina-status', ws?.publicId],
    queryFn: () => api<DominaStatus>(`/workspaces/${ws!.publicId}/chat/domina/usage`),
    enabled: !!ws,
    staleTime: 30_000,
  });
}

interface DominaAttachmentPayload {
  name: string;
  mimeType: string;
  dataBase64: string;
}

/** Read a File as base64 (without the data: URL prefix). */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const res = typeof reader.result === 'string' ? reader.result : '';
      const comma = res.indexOf(',');
      resolve(comma >= 0 ? res.slice(comma + 1) : res);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

/* ── Local storage helpers ──────────────────────────────────────── */

function storageKey(wsId: string) {
  return `iheart:domina-history:${wsId}`;
}

function loadHistory(wsId: string): DominaMessage[] {
  try {
    const raw = localStorage.getItem(storageKey(wsId));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveHistory(wsId: string, messages: DominaMessage[]) {
  try {
    // Keep last 200 messages max
    const trimmed = messages.slice(-200);
    localStorage.setItem(storageKey(wsId), JSON.stringify(trimmed));
  } catch {
    // Ignore storage errors
  }
}

/* ── Hook ───────────────────────────────────────────────────────── */

export function useDominaChat() {
  const ws = useCurrentWorkspace();
  const wsId = ws?.publicId ?? '';
  const qc = useQueryClient();
  const [messages, setMessages] = useState<DominaMessage[]>(() => loadHistory(wsId));

  // Reset messages when workspace changes
  const [prevWsId, setPrevWsId] = useState(wsId);
  if (wsId !== prevWsId) {
    setPrevWsId(wsId);
    const loaded = loadHistory(wsId);
    setMessages(loaded);
  }

  // Lets the user interrupt Domina mid-thought: we abort the in-flight request
  // and "rewind" — remove the message we optimistically added and hand its text
  // back so it can be restored to the input for editing.
  const abortRef = useRef<AbortController | null>(null);
  const pendingRef = useRef<{ id: string; text: string } | null>(null);

  const mutation = useMutation({
    mutationFn: async (arg: string | { text: string; files?: File[] }) => {
      const userMessage = typeof arg === 'string' ? arg : arg.text;
      const files = typeof arg === 'string' ? [] : (arg.files ?? []);

      const userMsg: DominaMessage = {
        id: crypto.randomUUID().slice(0, 10),
        role: 'user',
        content: userMessage,
        timestamp: new Date().toISOString(),
        attachments: files.length ? files.map((f) => ({ name: f.name })) : undefined,
      };

      // Add user message to state immediately
      const updatedWithUser = [...messages, userMsg];
      setMessages(updatedWithUser);
      saveHistory(wsId, updatedWithUser);

      // Send to API (only role + content)
      const apiMessages = updatedWithUser.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      // Encode attachments (images she can see, documents she can read).
      const attachments: DominaAttachmentPayload[] = await Promise.all(
        files.map(async (f) => ({
          name: f.name,
          mimeType: f.type || 'application/octet-stream',
          dataBase64: await fileToBase64(f),
        })),
      );

      const controller = new AbortController();
      abortRef.current = controller;
      pendingRef.current = { id: userMsg.id, text: userMessage };

      let response: DominaApiResponse;
      try {
        response = await api<DominaApiResponse>(
          `/workspaces/${wsId}/chat/domina`,
          { method: 'POST', body: { messages: apiMessages, attachments }, signal: controller.signal },
        );
      } catch (err) {
        // Interrupted by the user — stop() already rewound the thread. Swallow.
        if (err instanceof DOMException && err.name === 'AbortError') return null;
        throw err;
      } finally {
        abortRef.current = null;
        pendingRef.current = null;
      }

      // Add Domina's response
      const dominaMsg: DominaMessage = {
        id: crypto.randomUUID().slice(0, 10),
        role: 'assistant',
        content: response.reply,
        confident: response.confident,
        images: response.images,
        timestamp: new Date().toISOString(),
      };

      const updatedWithResponse = [...updatedWithUser, dominaMsg];
      setMessages(updatedWithResponse);
      saveHistory(wsId, updatedWithResponse);

      // Each chat spends a credit — refresh the header balance so it counts down.
      qc.invalidateQueries({ queryKey: ['domina-status', wsId] });

      return response;
    },
  });

  /**
   * Stop Domina mid-thought. Aborts the request, removes the message we just
   * added, and returns its text so the caller can drop it back into the input.
   */
  const stop = useCallback((): string | null => {
    if (!abortRef.current) return null;
    const pending = pendingRef.current;
    abortRef.current.abort();
    abortRef.current = null;
    pendingRef.current = null;
    if (pending) {
      setMessages((prev) => {
        const next = prev.filter((m) => m.id !== pending.id);
        saveHistory(wsId, next);
        return next;
      });
      return pending.text;
    }
    return null;
  }, [wsId]);

  const clearHistory = useCallback(() => {
    setMessages([]);
    if (wsId) {
      try {
        localStorage.removeItem(storageKey(wsId));
      } catch { /* ignore */ }
    }
  }, [wsId]);

  return {
    messages,
    sendMessage: mutation.mutateAsync,
    isPending: mutation.isPending,
    stop,
    clearHistory,
    isAvailable: !!ws,
  };
}
